import { useEffect, useState } from 'react';
import { diffWords } from 'diff';
import { authHeaders } from '../../hooks/useAuth';

interface Revision {
  id: string;
  revision_number: number;
  title: string;
  content_html: string | null;
  created_at: string;
}

interface Props {
  pageSlug: string;
  onRevert: (revisionId: string) => void;
  onClose: () => void;
}

export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

export function htmlToText(html: string | null): string {
  if (!html) return '';
  return html
    .replace(/<img[^>]*alt="([^"]*)"[^>]*>/gi, '[image: $1]')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function DiffView({ a, b }: { a: string; b: string }) {
  const parts = diffWords(a, b);
  return (
    <div className="font-mono text-sm leading-relaxed whitespace-pre-wrap break-words">
      {parts.map((part, i) => {
        if (part.added)
          return <mark key={i} className="bg-green-100 text-green-900">{part.value}</mark>;
        if (part.removed)
          return <del key={i} className="bg-red-100 text-red-700 line-through">{part.value}</del>;
        return <span key={i} className="text-gray-700">{part.value}</span>;
      })}
    </div>
  );
}

export function RevisionHistoryPanel({ pageSlug, onRevert, onClose }: Props) {
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [comparing, setComparing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/v1/pages/${pageSlug}/revisions`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setRevisions)
      .finally(() => setLoading(false));
  }, [pageSlug]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
    setComparing(false);
  };

  const revA = revisions.find((r) => r.id === selected[0]);
  const revB = revisions.find((r) => r.id === selected[1]);

  return (
    <aside className="fixed right-0 top-0 h-full w-96 bg-theme-surface shadow-xl border-l border-theme-border flex flex-col z-50">
      <header className="flex items-center justify-between px-4 py-3 border-b border-theme-border">
        <h2 className="font-semibold text-theme-text-primary">Revision History</h2>
        <button
          onClick={onClose}
          aria-label="Close history panel"
          className="cursor-pointer rounded p-1 text-theme-text-muted hover:text-theme-text-primary transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-theme-accent"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">
        {loading && <p className="p-4 text-sm text-theme-text-muted">Loading…</p>}
        {!loading && revisions.length === 0 && (
          <p className="p-4 text-sm text-theme-text-muted">No revisions yet.</p>
        )}
        {revisions.map((rev) => (
          <div
            key={rev.id}
            className={`px-4 py-3 border-b border-theme-border flex items-start gap-2 transition-colors ${
              selected.includes(rev.id) ? 'bg-theme-accent/10' : 'hover:bg-theme-bg'
            }`}
          >
            <input
              type="checkbox"
              checked={selected.includes(rev.id)}
              onChange={() => toggleSelect(rev.id)}
              aria-label={`Select revision ${rev.revision_number}`}
              className="mt-1 shrink-0 cursor-pointer"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-theme-text-primary truncate">{rev.title}</p>
              <p className="text-xs text-theme-text-muted">
                #{rev.revision_number} · {relativeTime(rev.created_at)}
              </p>
            </div>
            <button
              onClick={() => onRevert(rev.id)}
              className="cursor-pointer text-xs font-semibold text-theme-accent hover:opacity-80 transition-opacity shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-theme-accent rounded"
            >
              Revert
            </button>
          </div>
        ))}
      </div>

      {selected.length === 2 && (
        <footer className="p-3 border-t border-theme-border">
          <button
            onClick={() => setComparing(true)}
            className="cursor-pointer w-full bg-theme-accent text-theme-accent-fg rounded-xl px-3 py-2 text-sm font-semibold hover:opacity-90 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-accent"
          >
            Compare selected revisions
          </button>
        </footer>
      )}

      {comparing && revA && revB && (
        <div className="absolute inset-0 bg-theme-surface flex flex-col z-10">
          <header className="flex items-center justify-between px-4 py-3 border-b border-theme-border">
            <h3 className="font-semibold text-theme-text-primary text-sm">
              #{revA.revision_number} → #{revB.revision_number}
            </h3>
            <button
              onClick={() => setComparing(false)}
              aria-label="Close diff view"
              className="cursor-pointer rounded p-1 text-theme-text-muted hover:text-theme-text-primary transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-theme-accent"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-4">
            <DiffView
              a={htmlToText(revA.content_html)}
              b={htmlToText(revB.content_html)}
            />
          </div>
        </div>
      )}
    </aside>
  );
}
